import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { EVENTO_API_401 } from '@core/api/client'
import { authApi, type MeResponse } from '@core/api/auth'
import { SESSAO_TERMINADA } from './avisos'
import { esquecerComigo } from '@core/partilhas/partilhasEstado'
import { cleared, estadoDoArranque } from './estadoAuth'
import {
  AuthContext,
  type AuthContextValue,
  type AuthState,
} from './useAuth'

const initial: AuthState = {
  mode: 'loading',
  user: null,
  workspace: null,
  modules: [],
  hasPassword: false,
  aviso: null,
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initial)

  const refresh = useCallback(async () => {
    try {
      const me: MeResponse = await authApi.me()
      setState(estadoDoArranque({ me }))
    } catch (err) {
      setState(estadoDoArranque({ erro: err }))
    }
  }, [])

  const continuarLocal = useCallback(() => {
    setState({ mode: 'local', ...cleared })
  }, [])

  // Um pedido a meio do trabalho recebeu 401. Confirma-se com /me (um
  // 401 também pode ser a chave do assistente recusada): se a sessão
  // acabou mesmo, volta-se à página de entrada com o motivo, em vez de
  // deixar os módulos a falhar um a um.
  useEffect(() => {
    let aVerificar = false
    const aoReceber401 = () => {
      if (aVerificar) return
      aVerificar = true
      void (async () => {
        try {
          await authApi.me()
        } catch (err) {
          const novo = estadoDoArranque({ erro: err })
          setState((actual) => {
            if (actual.mode !== 'cloud') return actual
            if (novo.mode === 'unauthenticated') {
              return { ...novo, aviso: SESSAO_TERMINADA }
            }
            return novo.mode === 'forbidden' ? novo : actual
          })
        } finally {
          aVerificar = false
        }
      })()
    }
    window.addEventListener(EVENTO_API_401, aoReceber401)
    return () => window.removeEventListener(EVENTO_API_401, aoReceber401)
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // ignore: clearing client state is what matters here
    }
    // O que outros partilharam com esta pessoa não fica à vista de quem
    // entrar a seguir no mesmo browser.
    esquecerComigo()
    setState({ mode: 'unauthenticated', ...cleared })
  }, [])

  useEffect(() => {
    // A regra não vê para lá do `await`: o `refresh` só escreve
    // estado depois de `await authApi.me()`, por isso não há render em
    // cascata para evitar aqui.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh()
  }, [refresh])

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, refresh, logout, continuarLocal }),
    [state, refresh, logout, continuarLocal],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
